import type {
  AIProvider,
  DesignBrief,
  DesignDirective,
  ImageAnalysis,
  RecipeId,
} from '@/types';
import { getStyle } from '../layout/styles';
import { pickEmphasisWords } from '../typography/textOptimizer';

/**
 * The default design intelligence: deterministic rules over the local image
 * analysis. No API key, no network, no latency.
 *
 * This is not a stub that returns placeholder data — it makes real decisions
 * from real measurements, and it is what ships when no vision model is
 * configured. The Claude provider replaces it for judgement calls that need
 * semantics (what the photo is *of*, which words carry the meaning), and falls
 * back to this one on any failure.
 */
export class HeuristicProvider implements AIProvider {
  id = 'heuristic';
  label = 'Built-in design engine';

  available(): boolean {
    return true;
  }

  async analyze(
    _imageDataUrl: string,
    analysis: ImageAnalysis,
    brief: DesignBrief,
  ): Promise<DesignDirective> {
    return heuristicDirective(analysis, brief);
  }
}

export function heuristicDirective(
  analysis: ImageAnalysis,
  brief: DesignBrief,
): DesignDirective {
  const style = getStyle(brief.styleId);
  const notes: string[] = [];

  // --- Zone -------------------------------------------------------------
  // The analyzer already ranked zones by calmness and distance from faces and
  // the focal mass. Take the best, but prefer a side column when the subject
  // is clearly lateral — that reads as intentional composition rather than as
  // text dropped wherever there happened to be room.
  const best = analysis.zones[0];
  const subjectCx = analysis.subject.x + analysis.subject.w / 2;
  const lateral = Math.abs(subjectCx - 0.5) > 0.16;
  const oppositeSide = subjectCx > 0.5 ? 'left' : 'right';
  const oppositeZone = analysis.zones.find((z) => z.id === oppositeSide);

  let textZone = best.id;
  if (lateral && oppositeZone && oppositeZone.faceOverlap < 0.05 && oppositeZone.score > best.score - 0.12) {
    textZone = oppositeSide;
    notes.push(
      `Subject sits ${subjectCx > 0.5 ? 'right' : 'left'} of center — moved the text to the open ${oppositeSide} side.`,
    );
  } else {
    notes.push(`Chose the ${best.label.toLowerCase()} as the calmest place for type.`);
  }

  const zone = analysis.zones.find((z) => z.id === textZone) ?? best;

  // --- Color scheme -----------------------------------------------------
  const textColorScheme: 'light' | 'dark' = zone.luminance < 0.52 ? 'light' : 'dark';

  // --- Overlay ----------------------------------------------------------
  let overlayTreatment: DesignDirective['overlayTreatment'] = 'scrim';
  if (zone.detail > 0.24) {
    overlayTreatment = 'blur-panel';
    notes.push('That region is visually busy — a blurred panel keeps the type legible.');
  } else if (zone.detail > 0.15) {
    overlayTreatment = 'panel';
  } else if (zone.detail < 0.07 && analysis.contrastSpread < 0.4) {
    overlayTreatment = 'none';
    notes.push('The background is even enough to carry text directly.');
  }

  const overlayStrength = Math.min(
    0.92,
    style.overlayStrength + (analysis.busy ? 0.12 : 0) + zone.detail * 0.6,
  );

  // --- Recipes ----------------------------------------------------------
  const recipes: RecipeId[] = [];
  if (analysis.faces.length > 0) {
    // With a face in frame, prioritise compositions that keep type well clear
    // of it rather than centered layouts that tend to cross the subject.
    const faceCy = analysis.faces[0].y + analysis.faces[0].h / 2;
    if (faceCy < 0.55) recipes.push('lower-third', 'editorial-stack');
    else recipes.push('top-kicker', 'corner-anchor');
    if (lateral) recipes.push('side-panel');
    notes.push('Face detected — layouts that would cross it were dropped.');
  }
  if (lateral) recipes.push('side-panel');
  for (const r of style.recipes) if (!recipes.includes(r)) recipes.push(r);

  // --- Accent -----------------------------------------------------------
  const accentSource =
    analysis.dominantColors.find((c) => {
      const r = parseInt(c.hex.slice(1, 3), 16);
      const g = parseInt(c.hex.slice(3, 5), 16);
      const b = parseInt(c.hex.slice(5, 7), 16);
      return Math.max(r, g, b) - Math.min(r, g, b) > 30;
    }) ?? analysis.dominantColors[0];

  // --- Logo -------------------------------------------------------------
  const zoneIsTop = zone.rect.y < 0.4;
  const logoCorner = zoneIsTop
    ? subjectCx > 0.5
      ? 'bottom-left'
      : 'bottom-right'
    : subjectCx > 0.5
      ? 'top-left'
      : 'top-right';

  return {
    recipes: recipes.slice(0, 6),
    textZone,
    textColorScheme,
    overlayTreatment,
    overlayStrength,
    accentColor: accentSource?.hex ?? '#c8a882',
    emphasisWords: style.useEmphasis
      ? pickEmphasisWords(brief.title, 2).map((i) => brief.title.trim().split(/\s+/)[i])
      : [],
    headlineWeight: style.headlineWeight,
    headlineCase: style.headlineCase,
    tracking: style.headlineTracking,
    logoCorner,
    notes,
  };
}
